// The one join between a view plan's `focus` and the board's own key space.
//
// The plan keys on ORIGIN qualified names; the board keys on ids, and one
// origin fans out to every `::m::` copy on a `group by` board — so a focused
// module is figure in every group it joins. `ProjectionResult.nodeIdsByOrigin`
// carries that mapping and this is the only place it is applied to focus.
//
// Lives beside the resolver rather than inside `BoardPane` because it is the
// half of the figure/ground contract a test can actually pin: the component
// half is a memo in a 900-line pane.

/**
 * Board keys for a view's focus set — `undefined` when the board must not
 * engage focus at all.
 *
 * A key the index does not know is DROPPED, not passed through as itself. That
 * fallback is what turns one unrenderable focus key into a blank board: the
 * resolver marks the matches figure and everything else GROUND, so a set whose
 * every member matches nothing veils the entire canvas — emphasis inverted
 * into erasure. Dropping instead means the degenerate case empties the set,
 * and an empty set is already the "no focus" signal every consumer honours
 * (`makeBoardFocus` returns null, `applyFocus` returns the space by reference).
 *
 * The engine keeps stale keys out in the first place — `focus` only ever marks
 * something it draws — so this is the backstop, not the mechanism. It matters
 * because the failure is silent and total: no diagnostic, no missing element,
 * just a board that looks switched off.
 *
 * `nodeIdsByOrigin` absent (no projection ran) means there is nothing to map
 * against, and the keys pass through as board keys unchanged.
 */
export function boardFocusKeys(
  focus: ReadonlySet<string> | undefined,
  nodeIdsByOrigin: ReadonlyMap<string, readonly string[]> | null | undefined
): ReadonlySet<string> | undefined {
  if (!focus || focus.size === 0) return undefined;
  const ids = nodeIdsByOrigin
    ? [...focus].flatMap((k) => nodeIdsByOrigin.get(k) ?? [])
    : [...focus];
  return ids.length > 0 ? new Set(ids) : undefined;
}
